<template>
  <div
    id="iframe-root"
    :class="{ 'is-iframe': isInIframe }"
  >
    <HeroBanner v-if="!isLite && !isWidget" />
    <AppHeader v-if="!isWidget" />

    <div
      class="app-content"
      :class="{ 'app-content--lite': isLite }"
    >
      <router-view @open-betslip-modal="openBetslipModal" />
      <AppFooter v-if="showAppFooter && !isLite && !isWidget" />
      <VirtualKeyboardHost />
      <GlobalLoader />
      <ToastComponent
        v-if="toast"
        :centered="toast.centered"
        :title="toast.title"
        :message="toast.message"
        :type="toast.type"
        :duration="toast.duration"
      />
      <BetslipModal
        :is-open="modalStore.showBetslip"
        @close="closeBetslipModal"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, watch, ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import HeroBanner from './components/base/HeroBanner.vue';
import AppHeader from '@/components/base/AppHeader.vue'
import AppFooter from './components/base/AppFooter.vue';
import ToastComponent from '@/components/ToastComponent.vue'
import { useToast } from '@/composables/useToast'
import { useI18n } from 'vue-i18n'
import BetslipModal from '@/components/BetslipModal.vue'
import VirtualKeyboardHost from '@/components/VirtualKeyboardHost.vue'
import GlobalLoader from '@/components/GlobalLoader.vue'
import { useModalStore } from '@/stores/modal'
import { useAuthStore } from '@/stores/auth'
import { useGameStore } from '@/stores/game'
import { useBetslipStore } from '@/stores/betslip'
import { getThemeParamFromUrl, initializeTheme, setTheme } from '@/composables/useTheme'
import { resolveThemeName } from '@/utils/themeLoader'
import { usePromotionStore } from '@/stores/promotion'
import { normalizePromotionConfig } from '@/types/promotion'
import { iframeCommunication } from '@/utils/iframeCommunication'
import { persistIntegrationMode, hasIntegrationQueryInLocation } from '@/utils/integrationMode'
import { setApiBaseUrl } from '@/api'
import { useIntegrationMode } from '@/composables/useIntegrationMode'
import { usePromotionSync } from '@/composables/usePromotionSync'
import { useUpcomingDrawsStore } from '@/stores/upcomingDraws'

const { toast, showToast } = useToast()
const { t } = useI18n()
const modalStore = useModalStore()
const authStore = useAuthStore()
const gameStore = useGameStore()
const betslipStore = useBetslipStore()
const promotionStore = usePromotionStore()
const upcomingDrawsStore = useUpcomingDrawsStore()
const { refreshPromotions, applyPartnerPromotion } = usePromotionSync(showToast)
const route = useRoute()
const router = useRouter()
const iframeCleanupHandlers: Array<() => void> = []

const isInitialized = ref(false)
const viewportWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024)

const { isLite } = useIntegrationMode()
const isWidget = computed(() => route.name === 'widget-upcoming')

const showAppFooter = computed(() => {
  if (route.name !== 'lottery-detail') return true
  return viewportWidth.value > 768
})

const onViewportResize = () => {
  viewportWidth.value = window.innerWidth
}

const openBetslipModal = () => {
  modalStore.openBetslip()
}

const closeBetslipModal = () => {
  modalStore.closeBetslip()
}

const refreshAccountScopedCatalog = async () => {
  upcomingDrawsStore.invalidateAccountContext()
  await upcomingDrawsStore.fetchDraws(1, undefined, true)
  await upcomingDrawsStore.fetchFeaturedDraws(1, undefined, true)
  gameStore.clearProducts()
}

watch(
  () => authStore.account,
  async (newAccount, oldAccount) => {
    if (!newAccount || newAccount === oldAccount) {
      return
    }
    try {
      await refreshAccountScopedCatalog()
    } catch (error) {
      console.error('Failed to refresh draws after account change:', error)
    }
  },
  { immediate: false }
)

// Watch for account changes in URL
watch(
  () => route.query.account,
  async (newAccount, oldAccount) => {
    if (newAccount !== oldAccount && newAccount && typeof newAccount === 'string') {
      authStore.account = newAccount
      sessionStorage.setItem('auth_account', newAccount)
      betslipStore.syncStorageContext()
      
      try {
        await initializeTheme(true)
        const currency = sessionStorage.getItem('auth_currency')
        if (currency) {
          await gameStore.fetchGameSettings(false)
        }
        await refreshPromotions(undefined, { silent: true })
        await refreshAccountScopedCatalog()
      } catch (error) {
        console.error('Failed to re-initialize theme:', error)
      }
    }
  },
  { immediate: false }
)

watch(
  () => route.query.currency,
  async (newCurrency, oldCurrency) => {
    if (newCurrency !== oldCurrency && newCurrency && typeof newCurrency === 'string') {
      const cleanCurrency = newCurrency.split('?')[0]
      sessionStorage.setItem('auth_currency', cleanCurrency)
      betslipStore.syncStorageContext()

      try {
        if (authStore.account) {
          await gameStore.fetchGameSettings(false, true)
        }
        await refreshPromotions(undefined, { silent: true })
      } catch (error) {
        console.error('Failed to refresh game settings after currency change:', error)
      }
    }
  },
  { immediate: false }
)

// Only apply game-settings theme when URL has no theme param (URL wins)
watch(
  () => gameStore.getBetSettings?.theme,
  async (newTheme) => {
    if (newTheme && !getThemeParamFromUrl()) {
      try {
        await setTheme(resolveThemeName(newTheme))
      } catch (error) {
        console.error('Failed to update theme from game settings:', error)
      }
    }
  },
  { immediate: false }
)

const isInIframe = iframeCommunication.isRunningInIframe()

watch(isLite, (lite) => {
  if (typeof document === 'undefined') return
  document.body.classList.toggle('integration-lite', lite)
}, { immediate: true })

watch(isWidget, (widget) => {
  if (typeof document === 'undefined') return
  document.body.classList.toggle('integration-widget', widget)
}, { immediate: true })

watch(
  () => [authStore.token, gameStore.getBetSettings?.enabled_modules, gameStore.getBetSettings?.promotion_source],
  async () => {
    if (!isInitialized.value) return
    if (!gameStore.getBetSettings) return
    await refreshPromotions(undefined, { silent: true })
  }
)

onMounted(async () => {
  window.addEventListener('resize', onViewportResize)
  const handleParentLogout = () => {
    authStore.logout({ silent: true, reason: 'host-request' })
    modalStore.closeBetslip()
    showToast({
      title: t('auth.loggedOut'),
      message: t('auth.sessionClosed'),
      type: 'info',
      duration: 4000
    })
    router.replace({ path: '/next-draws', query: {} })
  }

  const handleParentNavigate = (data?: Record<string, unknown>) => {
    try {
      if (!data) return
      const target = data.route || data.path || data.name
      if (!target || typeof target !== 'string') return
      
      const query = (data.query && typeof data.query === 'object') 
        ? data.query as Record<string, string> 
        : undefined

      if (target.startsWith('/')) {
        router.push({ path: target, query })
      } else {
        router.push({ name: target, query })
      }
    } catch (e) {
      console.error('Iframe navigation failed:', e)
    }
  }

  // CORE HANDSHAKE HANDLER: This is where configuration is synchronized
  const handleParentReady = async (data?: Record<string, unknown>) => {
    console.log('[App] Handshake received from parent:', data)
    if (data?.config && typeof data.config === 'object') {
      const config = data.config as Record<string, any>

      authStore.applyParentConfig(config)
      betslipStore.syncStorageContext()

      if (config.GAME_SERVER_URL) {
        setApiBaseUrl(config.GAME_SERVER_URL)
      }

      try {
        await initializeTheme(true)
        const currency = config.currency || sessionStorage.getItem('auth_currency')
        if (currency) {
          await gameStore.fetchGameSettings(false)
        }
        await refreshPromotions(config, { silent: true })
      } catch (e) {
        console.warn('Initialization after handshake failed:', e)
      }
    } else {
      await initializeTheme()
    }

    isInitialized.value = true
  }

  const handleClearBetslip = () => {
    betslipStore.clearBetslip()
    showToast({
      title: t('toast.betslipCleared'),
      message: t('toast.betslipClearedMsg'),
      type: 'info',
      duration: 3000
    })
  }

  const handlePromotionAvailable = (data?: Record<string, unknown>) => {
    if (!data) return
    applyPartnerPromotion({ promotion: data })
  }

  const handlePromotionCleared = () => {
    promotionStore.clear('partner_cleared')
  }

  const handlePlacePromotion = (data?: Record<string, unknown>) => {
    if (!data) return
    const currency =
      (typeof data.currency === 'string' && data.currency) ||
      sessionStorage.getItem('auth_currency') ||
      ''
    const stake = Number(data.stake ?? promotionStore.activePromotion?.stake)
    const normalized = normalizePromotionConfig({
      ...data,
      stake,
      currency,
    })
    if (!normalized) return
    promotionStore.apply(normalized)
    promotionStore.setApplyToSlip(true)
    window.dispatchEvent(new CustomEvent('bitville:place-promotion'))
  }

  // Register communication listeners
  iframeCleanupHandlers.push(iframeCommunication.on('logout', handleParentLogout))
  iframeCleanupHandlers.push(iframeCommunication.on('navigate', handleParentNavigate))
  iframeCleanupHandlers.push(iframeCommunication.on('parent-ready', handleParentReady))
  iframeCleanupHandlers.push(iframeCommunication.on('clear-betslip', handleClearBetslip))
  iframeCleanupHandlers.push(iframeCommunication.on('promotion-available', handlePromotionAvailable))
  iframeCleanupHandlers.push(iframeCommunication.on('promotion-cleared', handlePromotionCleared))
  iframeCleanupHandlers.push(iframeCommunication.on('place-promotion', handlePlacePromotion))

  if (isInIframe) {
    if (!hasIntegrationQueryInLocation()) {
      persistIntegrationMode(null)
    }
    document.body.classList.add('iframe-mode')
    document.documentElement.classList.add('iframe-mode')
    
    // Measure and report height
    iframeCommunication.startObserving('iframe-root')
    
    // START HANDSHAKE: Tell parent child is ready to receive configuration
    iframeCommunication.sendReady()

    // Safety timeout: If handshake doesn't complete in 4s, proceed with default init
    setTimeout(async () => {
      if (!isInitialized.value) {
        console.warn('[App] Handshake timeout - proceeding with default initialization')
        await initializeTheme()
        await refreshAccountScopedCatalog()
        isInitialized.value = true
      }
    }, 4000)

    // Handle deep links from URL params
    const gameSlug = (route.query.game || route.query.gameSlug) as string | undefined
    if (gameSlug && typeof gameSlug === 'string' && gameSlug.trim()) {
      const slug = gameSlug.trim()
      const restQuery = { ...route.query }
      delete restQuery.game
      delete restQuery.gameSlug
      router.replace({ path: `/lottery/${slug}`, query: restQuery as Record<string, string> })
    }
  } else {
    // Standard standalone mode
    const account = authStore.account || sessionStorage.getItem('auth_account') || (route.query.account as string)
    if (account) {
      authStore.account = account
      sessionStorage.setItem('auth_account', account)
      betslipStore.syncStorageContext()
    }

    const token = (route.query.token as string)
    if (token) {
      authStore.token = token
      sessionStorage.setItem('auth_token', token)
    }

    const currencyQuery = route.query.currency as string | undefined
    if (currencyQuery && typeof currencyQuery === 'string' && currencyQuery.trim()) {
      const cleanCurrency = currencyQuery.split('?')[0]
      sessionStorage.setItem('auth_currency', cleanCurrency)
      betslipStore.syncStorageContext()
    }

    try {
      await initializeTheme()
      const currency = sessionStorage.getItem('auth_currency')
      if (account && currency) {
        await gameStore.fetchGameSettings(false)
        authStore.refreshAuthFromExternalSources()
        await refreshPromotions(undefined, { silent: true })
      }
    } catch (e) {
      console.warn('Failed to load initial settings:', e)
    }

    isInitialized.value = true
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', onViewportResize)
  iframeCommunication.destroy()
  iframeCleanupHandlers.forEach((cleanup) => cleanup())
})

defineExpose({
  openBetslipModal,
  closeBetslipModal
})
</script>

<style>
body.integration-lite #app {
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

body.integration-widget #app {
  background: var(--bg-primary, var(--colour-surface));
  color: var(--text-primary, var(--colour-base-text-colour));
}

body.integration-widget .app-content {
  padding: 0;
  max-width: none;
  background: var(--bg-primary, var(--colour-surface));
}

body.integration-widget .app-content,
body.integration-widget #iframe-root {
  min-height: auto;
}

.app-content--lite {
  padding-top: 0;
}

body.integration-lite .country-flag,
body.integration-lite .mobile-flag,
body.integration-lite .cancelled-stamp {
  display: none !important;
}

body.integration-lite .timer-icon-lite,
body.integration-lite .expand-chevron,
body.integration-lite .cancelled-stamp-lite {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 1;
}

body.integration-lite .cancelled-stamp-lite {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--colour-base-text-colour, #111);
  padding: 4px 8px;
  border: 1px solid currentColor;
}

#app {
  width: var(--app-width, 100%);
  max-width: var(--app-max-width, 1280px);
  margin: 0 auto;
  background: var(--colour-surface) !important;
  box-sizing: border-box;
  min-height: 100vh;
  font-family: 'Ubuntu', sans-serif;
  position: relative;
  color: var(--colour-base-text-colour);
  overflow-x: hidden;
}

/* Sticky detail header needs a non-hidden overflow ancestor chain. */
body.detail-sticky-header #app {
  overflow-x: clip;
}

body.detail-sticky-header.iframe-mode #app,
body.iframe-mode.detail-sticky-header #app {
  overflow-x: clip !important;
}

/* Default iframe mode = parent/host page scrolls (content-height iframe).
   Keep overflow-y:auto as a fallback when the host still caps iframe height;
   never lock height to 100% or use overscroll-behavior:contain (those trap touch). */
html.iframe-mode {
  overflow-x: hidden !important;
  overflow-y: auto !important;
  height: auto !important;
  min-height: 0 !important;
  max-height: none !important;
  overscroll-behavior-y: auto;
  touch-action: auto;
}

/* Opt-in: loader config scrollInsideIframe=true adds .iframe-scroll-inside */
html.iframe-mode.iframe-scroll-inside {
  overflow-y: auto !important;
  height: 100% !important;
  min-height: 100% !important;
  max-height: 100% !important;
  -webkit-overflow-scrolling: touch !important;
  overscroll-behavior-y: contain;
  touch-action: pan-y;
}

/* Higher specificity than stray `body.iframe-mode { position:fixed }` rules
   emitted by broken `:global(body.iframe-mode) .child` scoped CSS. */
html.iframe-mode body.iframe-mode {
  position: static !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
  z-index: auto !important;
  overflow-x: hidden !important;
  overflow-y: visible !important;
  height: auto !important;
  min-height: 0 !important;
  max-height: none !important;
  overscroll-behavior-y: auto;
  touch-action: auto;
}

html.iframe-mode.iframe-scroll-inside body.iframe-mode {
  min-height: 100% !important;
  -webkit-overflow-scrolling: touch !important;
  overscroll-behavior-y: contain;
  touch-action: pan-y;
}

body.iframe-mode #app {
  min-height: 0 !important;
  height: auto !important;
  /* Themes can cap width (e.g. supersportbet 1264px); default stays full-bleed. */
  width: var(--app-width, 100%) !important;
  max-width: var(--app-max-width, none) !important;
  margin: 0 auto !important;
  background: transparent !important;
  display: block !important;
  overflow: visible !important; /* Allows children to expand for measurement */
  overflow-x: hidden !important;
  box-sizing: border-box !important;
  touch-action: auto;
}

body.iframe-mode #iframe-root,
body.iframe-mode .is-iframe {
  width: 100% !important;
  max-width: none !important;
  margin: 0 !important;
  box-sizing: border-box !important;
  height: auto !important;
  overflow: visible !important;
  touch-action: auto;
}

body.iframe-mode .app-content {
  width: 100% !important;
  max-width: none !important;
  margin: 0 !important;
  padding: 0 !important;
  box-sizing: border-box !important;
}

body.iframe-mode .app-content,
body.iframe-mode .main-content-wrapper,
body.iframe-mode .betslip-wrapper,
body.iframe-mode .detail-view,
body.iframe-mode .upcoming-draws-view,
body.iframe-mode .betslip,
body.iframe-mode .betslip-content,
body.iframe-mode .selected-draws-container,
body.iframe-mode .draw-dropdown-menu,
body.iframe-mode .modal-content,
body.iframe-mode .modal-body,
body.iframe-mode .modal-box,
body.iframe-mode .modal-overlay,
body.iframe-mode .betslip-modal,
body.iframe-mode .mobile-back-container {
  height: auto !important;
  min-height: 0 !important;
  max-height: none !important;
  overflow: visible !important;
}

/* Native date inputs must stay a full hit target (not clipped) for picker UI in embeds */
body.iframe-mode .next-draws-filter {
  position: relative !important;
  overflow: visible !important;
}

body.iframe-mode .next-draws-filter .native-date-input {
  pointer-events: auto !important;
  color: transparent !important;
}

/* Horizontal card row: contain only x-axis overscroll so the host page
   does not pan sideways; never trap vertical page scroll. */
body.iframe-mode .lottery-grid.vertical {
  overscroll-behavior-x: contain;
}

/* Restore fixed/absolute positioning for overlays */
body.iframe-mode .modal-overlay,
body.iframe-mode .desktop-modal-overlay {
  position: fixed !important;
  background-color: var(--shadow-color) !important;
  z-index: 9999 !important;
  align-items: flex-start !important;
  padding-top: 40px !important;
}

/* Fullscreen modals (wager details, etc.) start at the top like betslip */
body.iframe-mode .modal-overlay--fullscreen {
  padding: 0 !important;
  padding-top: 0 !important;
  align-items: stretch !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  height: 100% !important;
  max-height: 100% !important;
}

/* Restore modal scrolling in iframe mode (overrides the bulk overflow:visible rule above) */
body.iframe-mode .modal-overlay {
  overflow-y: auto !important;
  height: 100% !important;
}

body.iframe-mode .modal-box {
  max-height: calc(100vh - 80px) !important;
  display: flex !important;
  flex-direction: column !important;
}

body.iframe-mode .modal-overlay--fullscreen .modal-box,
body.iframe-mode .modal-box--fullscreen {
  max-width: none !important;
  width: 100% !important;
  margin: 0 !important;
  border-radius: 0 !important;
  min-height: 100% !important;
  max-height: 100% !important;
  height: 100% !important;
}

body.iframe-mode .modal-body {
  overflow-y: auto !important;
  flex: 1 !important;
  min-height: 0 !important;
}

/* Lock background scroll when a modal is open in iframe mode */
html.iframe-mode.modal-open,
body.iframe-mode.modal-open {
  overflow: hidden !important;
}

/* Mobile detail: pin View Bets to the visible viewport (teleported to body). */
@media (max-width: 768px) {
  .mobile-view-bets-container,
  body.iframe-mode .mobile-view-bets-container {
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px)) !important;
    background: var(--colour-surface-cont-low, var(--surface-detail-header)) !important;
    border-top: 1px solid var(--tab-button-inactive, var(--colour-surface-cont)) !important;
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.12) !important;
    box-sizing: border-box !important;
    /* Above sticky detail header (120); below betslip panel (10000+) */
    z-index: 500 !important;
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
    height: auto !important;
    min-height: unset !important;
    max-height: none !important;
    overflow: visible !important;
    pointer-events: auto !important;
  }

  /* Hide View Bets while the mobile betslip panel is open */
  body:has(.mobile-betslip-panel) .mobile-view-bets-container {
    display: none !important;
  }
}

/* Strip all internal scrollbars globally in iframe mode */
body.iframe-mode * {
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}

body.iframe-mode *::-webkit-scrollbar {
  display: none !important;
  width: 0 !important;
  height: 0 !important;
}

body.iframe-mode {
  margin: 0;
  padding: 0;
}


/* .app-content {
  padding: 8px;
} */

.is-iframe .app-content {
  padding: 0;
}

@media (max-width: 768px) {
  #app,
  body.iframe-mode #app {
    width: 100% !important;
    max-width: 100% !important;
    margin: 0;
    padding: 0;
  }
  body:not(.iframe-mode) .app-content {
    padding: 8px;
    margin: 0px;
  }
}

::-webkit-scrollbar {
  display: none;
}

.desktop-only {
  display: block;
}

.mobile-only {
  display: none;
}

@media (max-width: 768px) {
  .desktop-only {
    display: none;
  }
  
  .mobile-only {
    display: block;
  }
}

::-webkit-scrollbar-thumb {
  display: none;
}

::-webkit-scrollbar-track {
  display: none;
}

::-webkit-scrollbar-corner {
  display: none;
}

::-webkit-scrollbar-button {
  display: none;
}

::-webkit-scrollbar-track-piece {
  display: none;
}

html, body {
  margin: 0;
  padding: 0;
  min-height: 100vh;
  overflow-x: hidden;
}

body {
  background: var(--colour-surface) !important;
}
</style>
